import React, { useEffect, useRef, useState } from 'react';
import { Badge, Popover, List, ListItem, ListItemText, Typography, Box } from '@mui/material';
import Notifications from '@mui/icons-material/Notifications';
import { API_BASE_URL, getAuthHeaders } from '../services/api';

const POLL_MS = 30000;

// Self-contained: polls its own unread count and fetches its own list on
// open. Fits into SideNav/AuthorizerSideNav's footer icon-button row (same
// visual slot as Change Password / Logout).
export default function NotificationBell({ collapsed }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const pollRef = useRef(null);

  const fetchUnreadCount = () => {
    fetch(`${API_BASE_URL}/notifications/unread-count`, { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => setUnreadCount(data.success ? data.count : 0))
      .catch(() => {});
  };

  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetch(`${API_BASE_URL}/notifications`, { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => setItems(data.success ? (data.data || []) : []))
      .catch(() => setItems([]));
  };

  const handleClose = () => {
    setAnchorEl(null);
    fetch(`${API_BASE_URL}/notifications/read-all`, { method: 'PATCH', headers: getAuthHeaders() })
      .then(() => setUnreadCount(0))
      .catch(() => {});
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={`sn-footer-btn sn-footer-btn--notifications${collapsed ? ' sn-footer-btn--icon' : ''}`}
        title="Notifications"
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <Notifications sx={{ fontSize: 18 }} />
        </Badge>
        {!collapsed && 'Notifications'}
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ width: 360, maxHeight: 420, overflowY: 'auto' }}>
          <Typography variant="subtitle2" sx={{ p: 1.5, pb: 0.5 }}>Notifications</Typography>
          {items.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>No notifications yet.</Typography>
          ) : (
            <List dense>
              {items.map((n) => (
                <ListItem key={n.id} divider>
                  <ListItemText
                    primary={n.title}
                    secondary={
                      <>
                        {n.message}
                        <br />
                        <Typography component="span" variant="caption" color="textSecondary">
                          {new Date(n.created_at).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
